/**
 * Google OAuth configuration parsing from CLI arguments and environment variables.
 *
 * This module provides utilities to parse Google OAuth configuration from
 * CLI arguments and environment variables, following the same pattern as @mcp-z/server's
 * parseConfig().
 */

import { resolve } from 'path';
import { parseArgs } from 'util';
import type { DcrConfig, OAuthConfig } from '../types.ts';

// Re-export for direct imports from config.ts
export type { DcrConfig, OAuthConfig };

/**
 * auth mode type (from OAuthConfig)
 */
type AuthMode = 'loopback-oauth' | 'service-account' | 'dcr';

/**
 * Parse auth mode string into auth mode.
 *
 * @param value - Auth mode string ('loopback-oauth', 'service-account', or 'dcr')
 * @returns Parsed auth mode
 * @throws Error if value is invalid
 *
 * @example Valid formats
 * ```typescript
 * parseAuthMode('loopback-oauth')   // { auth: 'loopback-oauth' }
 * parseAuthMode('service-account')  // { auth: 'service-account' }
 * parseAuthMode('dcr')              // { auth: 'dcr' }
 * ```
 */
function parseAuthMode(value: string): {
  auth: AuthMode;
} {
  if (value !== 'loopback-oauth' && value !== 'service-account' && value !== 'dcr') {
    throw new Error(`Invalid --auth value: "${value}". Valid values: loopback-oauth, service-account, dcr`);
  }

  return {
    auth: value as AuthMode,
  };
}

/**
 * Transport type for MCP servers
 *
 * @typedef {('stdio' | 'http')} TransportType
 * @property {'stdio'} stdio - Standard input/output transport for CLI applications
 * @property {'http'} http - HTTP transport for web-based applications
 */
export type TransportType = 'stdio' | 'http';

/**
 * Parse Google OAuth configuration from CLI arguments and environment variables.
 *
 * CLI Arguments:
 * - --auth: Auth mode ('loopback-oauth' | 'service-account' | 'dcr')
 *   - Default: 'loopback-oauth' (if flag is omitted)
 * - --headless: Disable browser opening for OAuth flow (default: false, true in test env)
 * - --redirect-uri: Override OAuth redirect URI (default: ephemeral loopback)
 * - --service-account-key-file: Service account key file path (required for service-account mode)
 *
 * Required environment variables:
 * - GOOGLE_CLIENT_ID: OAuth 2.0 client ID from Google Cloud Console
 *
 * Optional environment variables:
 * - GOOGLE_CLIENT_SECRET: OAuth 2.0 client secret (optional for public clients)
 * - AUTH_MODE: Auth mode (same format as --auth flag)
 * - HEADLESS: Headless mode flag ('true' to enable)
 * - REDIRECT_URI: OAuth redirect URI (overridden by --redirect-uri CLI flag)
 * - GOOGLE_SERVICE_ACCOUNT_KEY_FILE: Service account key file (for service-account mode)
 *
 * @param args - CLI arguments array (typically process.argv)
 * @param env - Environment variables object (typically process.env)
 * @param transport - Optional transport type. If 'stdio' and auth mode is 'dcr', throws an error.
 *                   See {@link TransportType} for valid values.
 * @returns Parsed Google OAuth configuration
 * @throws Error if required environment variables are missing, values are invalid, or DCR is used with stdio transport
 *
 * @example Default mode (no flags)
 * ```typescript
 * const config = parseConfig(process.argv, process.env);
 * // { auth: 'loopback-oauth' }
 * ```
 *
 * @example Override auth mode
 * ```typescript
 * parseConfig(['--auth=loopback-oauth'], process.env);
 * parseConfig(['--auth=service-account'], process.env);
 * parseConfig(['--auth=dcr'], process.env);
 * ```
 *
 * @example With transport validation
 * ```typescript
 * parseConfig(['--auth=dcr'], process.env, 'http'); // OK
 * parseConfig(['--auth=dcr'], process.env, 'stdio'); // Throws error
 * ```
 *
 * Valid auth modes:
 * - loopback-oauth (default)
 * - service-account
 * - dcr (HTTP transport only)
 */
export function parseConfig(args: string[], env: Record<string, string | undefined>, transport?: TransportType): OAuthConfig {
  function requiredEnv(key: string): string {
    const value = env[key];
    if (!value) {
      throw new Error(`Environment variable ${key} is required for Google OAuth`);
    }
    return value;
  }

  // Parse CLI arguments
  const { values } = parseArgs({
    args,
    options: {
      auth: { type: 'string' },
      headless: { type: 'boolean' },
      'redirect-uri': { type: 'string' },
      'service-account-key-file': { type: 'string' },
    },
    strict: false, // Allow other arguments
    allowPositionals: true,
  });

  const authArg = typeof values.auth === 'string' ? values.auth : undefined;
  const envAuthMode = env.AUTH_MODE;
  const mode = authArg || envAuthMode;

  let auth: AuthMode;

  if (mode) {
    const parsed = parseAuthMode(mode);
    auth = parsed.auth;
  } else {
    // DEFAULT: No flags provided, use loopback-oauth
    auth = 'loopback-oauth';
  }

  // Validate: DCR only works with HTTP transport
  if (auth === 'dcr' && transport === 'stdio') {
    throw new Error('DCR authentication mode requires HTTP transport. DCR is not supported with stdio transport.');
  }

  const cliRedirectUri = typeof values['redirect-uri'] === 'string' ? values['redirect-uri'] : undefined;
  const envRedirectUri = env.REDIRECT_URI;
  const redirectUri = cliRedirectUri ?? envRedirectUri;
  if (redirectUri && transport === 'stdio') {
    throw new Error('REDIRECT_URI requires HTTP transport. The OAuth callback must be served over HTTP.');
  }

  if (typeof values.headless === 'string') throw new Error('Use --headless or --no-headless (do not pass a value)');
  const cliHeadless = values['no-headless'] ? false : values.headless === true ? true : undefined;
  const envHeadless = env.HEADLESS === 'true' ? true : env.HEADLESS === 'false' ? false : undefined;
  const headless = cliHeadless ?? envHeadless ?? redirectUri !== undefined;

  const clientId = requiredEnv('GOOGLE_CLIENT_ID');
  const clientSecret = env.GOOGLE_CLIENT_SECRET;

  let serviceAccountKeyFile: string | undefined;
  if (auth === 'service-account') {
    const cliKeyFile = typeof values['service-account-key-file'] === 'string' ? values['service-account-key-file'] : undefined;
    serviceAccountKeyFile = cliKeyFile ?? env.GOOGLE_SERVICE_ACCOUNT_KEY_FILE;

    if (!serviceAccountKeyFile) {
      throw new Error('GOOGLE_SERVICE_ACCOUNT_KEY_FILE environment variable is required when using service account authentication. ' + 'Example: export GOOGLE_SERVICE_ACCOUNT_KEY_FILE=./service-account.json');
    }

    // Resolve relative paths now since cwd can change during execution
    serviceAccountKeyFile = resolve(serviceAccountKeyFile);
  }

  return {
    clientId,
    ...(clientSecret && { clientSecret }),
    auth,
    headless,
    ...(redirectUri && { redirectUri }),
    ...(serviceAccountKeyFile && { serviceAccountKeyFile }),
  };
}

/**
 * Build production configuration from process globals.
 * Entry point for production server.
 */
export function createConfig(): OAuthConfig {
  return parseConfig(process.argv, process.env);
}

/**
 * Parse DCR configuration from CLI arguments and environment variables.
 *
 * CLI Arguments:
 * - --dcr-mode: DCR mode ('self-hosted' | 'external')
 *   - Default: 'self-hosted' (if flag is omitted)
 * - --dcr-verify-url: External verification endpoint URL (required for external mode)
 * - --dcr-store-uri: DCR client storage URI (required for self-hosted mode)
 *
 * Required environment variables:
 * - GOOGLE_CLIENT_ID: OAuth 2.0 client ID from Google Cloud Console
 *
 * Optional environment variables:
 * - GOOGLE_CLIENT_SECRET: OAuth 2.0 client secret (optional for public clients)
 * - DCR_MODE: DCR mode (same format as --dcr-mode flag)
 * - DCR_VERIFY_URL: External verification URL (same as --dcr-verify-url flag)
 * - DCR_STORE_URI: DCR storage URI (same as --dcr-store-uri flag)
 *
 * @param args - CLI arguments array (typically process.argv)
 * @param env - Environment variables object (typically process.env)
 * @param scope - OAuth scopes to request (space-separated)
 * @returns Parsed DCR configuration
 * @throws Error if required environment variables are missing or validation fails
 *
 * @example Self-hosted mode
 * ```typescript
 * const config = parseDcrConfig(
 *   ['--dcr-mode=self-hosted', '--dcr-store-uri=file:///path/to/store.json'],
 *   process.env,
 *   'https://www.googleapis.com/auth/drive.readonly'
 * );
 * ```
 *
 * @example External mode
 * ```typescript
 * const config = parseDcrConfig(
 *   ['--dcr-mode=external', '--dcr-verify-url=https://auth0.example.com/verify'],
 *   process.env,
 *   'https://www.googleapis.com/auth/drive.readonly'
 * );
 * ```
 */
export function parseDcrConfig(args: string[], env: Record<string, string | undefined>, scope: string): DcrConfig {
  function requiredEnv(key: string): string {
    const value = env[key];
    if (!value) {
      throw new Error(`Environment variable ${key} is required for DCR configuration`);
    }
    return value;
  }

  const { values } = parseArgs({
    args,
    options: {
      'dcr-mode': { type: 'string' },
      'dcr-verify-url': { type: 'string' },
      'dcr-store-uri': { type: 'string' },
    },
    strict: false,
    allowPositionals: true,
  });

  const cliMode = typeof values['dcr-mode'] === 'string' ? values['dcr-mode'] : undefined;
  const envMode = env.DCR_MODE;
  const mode = cliMode || envMode || 'self-hosted';

  if (mode !== 'self-hosted' && mode !== 'external') {
    throw new Error(`Invalid --dcr-mode value: "${mode}". Valid values: self-hosted, external`);
  }

  const cliVerifyUrl = typeof values['dcr-verify-url'] === 'string' ? values['dcr-verify-url'] : undefined;
  const envVerifyUrl = env.DCR_VERIFY_URL;
  const verifyUrl = cliVerifyUrl || envVerifyUrl;

  const cliStoreUri = typeof values['dcr-store-uri'] === 'string' ? values['dcr-store-uri'] : undefined;
  const envStoreUri = env.DCR_STORE_URI;
  const storeUri = cliStoreUri || envStoreUri;

  if (mode === 'external' && !verifyUrl) {
    throw new Error('DCR external mode requires --dcr-verify-url or DCR_VERIFY_URL environment variable');
  }

  const clientId = requiredEnv('GOOGLE_CLIENT_ID');
  const clientSecret = env.GOOGLE_CLIENT_SECRET;

  return {
    mode,
    ...(verifyUrl && { verifyUrl }),
    ...(storeUri && { storeUri }),
    clientId,
    ...(clientSecret && { clientSecret }),
    scope,
  };
}
